import React, { createContext, useCallback, useContext } from 'react';

import { getMeta } from '@api';
import { useFetch } from '@hooks';
import { useEntries } from './useEntries';
import { useAuth, useUserSettings } from '@shared';

type Cleanup = () => void;

type AppContextType = {
  loading: boolean;
  meta: AppMetaType;

  title: React.ReactNode | null;
  removeTitle: (id: string) => void;
  addTitle: (node: React.ReactNode) => Cleanup;

  sidebarCollapsed: boolean;
  toggleSidebarCollapsed: () => void;

  refresh: () => void;
  setCampaignFilter: (id: string) => void;
};

const AppContext = createContext<AppContextType | undefined>(undefined);

const defaultMeta: AppMetaType = {
  campaigns: [],
  campaignsMap: {},
  campaignsData: [],

  initialCampaignFilter: '',
};

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, organization_id } = useAuth();
  const [initialFilter, setInitialFilter] = useUserSettings<string>(
    '',
    `App-Meta-[${organization_id}]-selectedCampaign`
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useUserSettings<boolean>(
    false,
    'App-Meta-sidebarCollapsed'
  );
  const setCampaignFilter = useCallback(
    (id: string) => setInitialFilter(id === '' ? 'empty' : id),
    [setInitialFilter]
  );
  const toggleSidebarCollapsed = useCallback(
    () => setSidebarCollapsed(prev => !prev),
    [setSidebarCollapsed]
  );

  const {
    data,
    refresh,
    initializing: loading,
  } = useFetch<AppMetaType>(user ? getMeta : () => defaultMeta, [user], {
    onSuccess: meta => {
      if (initialFilter === 'empty') return;

      const campaigns = meta.campaigns;
      if (campaigns.some(c => c.value === initialFilter)) return;

      setInitialFilter(campaigns[0]?.value || '');
    },
  });
  const [titles, addTitle, removeTitle] = useEntries<React.ReactNode>();

  const contextValue = React.useMemo<AppContextType>(
    () => ({
      loading,

      refresh,
      addTitle,
      removeTitle,
      setCampaignFilter,

      title: titles.at(-1)?.value || null,
      sidebarCollapsed,
      toggleSidebarCollapsed,
      meta: {
        ...(data || defaultMeta),
        initialCampaignFilter: initialFilter === 'empty' ? '' : initialFilter,
      },
    }),
    [
      data,
      loading,
      titles,
      initialFilter,
      sidebarCollapsed,
      toggleSidebarCollapsed,
      addTitle,
      removeTitle,
      refresh,
      setCampaignFilter,
    ]
  );

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);

  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }

  return context;
};
